import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import MobilePropertyCard from '../components/MobilePropertyCard';
import MobileProjectCard from '../components/MobileProjectCard';
import BuilderCard from '../components/BuilderCard';
import { FiMapPin, FiSearch, FiGrid, FiBriefcase, FiUser, FiActivity, FiHeart } from 'react-icons/fi';
import axios from 'axios';
import { generateSlug } from '../utils/slugify';
import { getImageUrl, getDefaultPropertyImage } from '../utils/imageUtils';

const MobileHome = () => {
  const [properties, setProperties] = useState([]);
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [projects, setProjects] = useState([]);
  const [builders, setBuilders] = useState([]);
  const [banners, setBanners] = useState([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [siteStats, setSiteStats] = useState(null);
  const [locationData, setLocationData] = useState({});
  const [loading, setLoading] = useState(true);
  
  const [templates, setTemplates] = useState([]);
  
  // Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchCountry, setSearchCountry] = useState('');
  const [searchState, setSearchState] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [searchType, setSearchType] = useState('');
  
  const countries = Object.keys(locationData).sort((a, b) => a.localeCompare(b));
  const states = searchCountry && locationData[searchCountry] ? Object.keys(locationData[searchCountry]).sort((a, b) => a.localeCompare(b)) : [];
  const cities = searchState && locationData[searchCountry] && locationData[searchCountry][searchState] ? [...locationData[searchCountry][searchState]].sort((a, b) => a.localeCompare(b)) : [];
  
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [propRes, projRes, builderRes, bannerRes, locRes, statsRes, wealthCustomersRes, wealthAgentsRes, tempRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/properties`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/projects`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/users/builders`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/banners`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/locations`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/stats`).catch(() => ({ data: null })),
          axios.get('https://api.wealthassociate.in/customer/allcustomers').catch(() => ({ data: null })),
          axios.get('https://api.wealthassociate.in/agent/allagents').catch(() => ({ data: null })),
          axios.get(`${process.env.REACT_APP_API_URL}/api/templates`).catch(() => ({ data: [] }))
        ]);
        
        let fetchedFeaturedProps = [];
        try {
          const featuredRes = await axios.get('https://api.wealthassociate.in/properties/getApproveProperty');
          const data = Array.isArray(featuredRes.data) ? featuredRes.data : featuredRes.data.properties || [];
          fetchedFeaturedProps = data.map(p => ({ ...p, isWealthProperty: true }));
        } catch (err) { }

        let fetchedFeaturedProjects = [];
        try {
          const endpoints = [
            'https://api.wealthassociate.in/coreproject/getallcoreprojects',
            'https://api.wealthassociate.in/coreproject/getallValueprojects',
            'https://api.wealthassociate.in/coreproject/getallWealthprojects',
            'https://api.wealthassociate.in/coreproject/getallPromotionalprojects'
          ];
          const responses = await Promise.allSettled(endpoints.map(url => axios.get(url)));
          responses.forEach(res => {
            if (res.status === 'fulfilled') {
              const data = Array.isArray(res.value.data) ? res.value.data : res.value.data.projects || [];
              fetchedFeaturedProjects = [...fetchedFeaturedProjects, ...data.map(p => ({ ...p, isWealthProject: true }))];
            }
          });
        } catch (err) { }

        setProperties(propRes.data);
        setFeaturedProperties(fetchedFeaturedProps);
        setProjects(projRes.data);
        setFeaturedProjects(fetchedFeaturedProjects);
        setBuilders(builderRes.data);
        setBanners(bannerRes.data);
        setLocationData(locRes.data);
        if (tempRes && tempRes.data) {
          setTemplates(tempRes.data);
        }
        
        if (statsRes.data) {
          const stats = { ...statsRes.data };
          stats.properties = {};
          
          const localBuyers = stats.users?.buyer || 0;
          const wealthBuyers = wealthCustomersRes?.data?.count || 0;
          const localAgents = stats.users?.agent || 0;
          const wealthAgents = wealthAgentsRes?.data?.total || wealthAgentsRes?.data?.count || 0;
          
          const uniqueDevelopers = new Set();
          if (projRes.data && Array.isArray(projRes.data)) {
            projRes.data.forEach(p => { if (p.builderName) uniqueDevelopers.add(p.builderName); });
          }
          fetchedFeaturedProjects.forEach(p => {
            if (p.companyName) uniqueDevelopers.add(p.companyName);
          });
          const developersCount = uniqueDevelopers.size;

          stats.users = {
            ...stats.users,
            buyer: localBuyers + wealthBuyers,
            agent: localAgents + wealthAgents,
            builder: developersCount > 0 ? developersCount : (stats.users?.builder || 0)
          };

          const totalProperties = (propRes.data?.length || 0) + (fetchedFeaturedProps.length || 0);
          const totalProjects = (projRes.data?.length || 0) + (fetchedFeaturedProjects.length || 0);

          if (totalProperties > 0) {
            stats.properties['Properties'] = {
              uploaded: totalProperties + 86,
              available: totalProperties,
              sold: 86
            };
          }
          if (totalProjects > 0) {
            stats.properties['Projects'] = {
              uploaded: totalProjects,
              available: totalProjects,
              sold: 0
            };
          }
          setSiteStats(stats);
        }
      } catch (err) {
        console.error("Failed to fetch data", err);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  // Banner carousel effect
  useEffect(() => {
    let interval;
    if (banners.length > 1) {
      interval = setInterval(() => {
        setCurrentBannerIndex(prev => (prev + 1) % banners.length);
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [banners]);

  // Fetch banners specifically when searchCity changes
  useEffect(() => {
    const fetchLocationBanners = async () => {
      try {
        const url = searchCity 
          ? `${process.env.REACT_APP_API_URL}/api/banners?location=${encodeURIComponent(searchCity)}` 
          : `${process.env.REACT_APP_API_URL}/api/banners`;
        const res = await axios.get(url);
        setBanners(res.data);
        setCurrentBannerIndex(0); // Reset index
      } catch (error) {
        console.error('Error fetching location banners:', error);
      }
    };
    fetchLocationBanners();
  }, [searchCity]);

  // Location Auto Detect
  useEffect(() => {
    if (Object.keys(locationData).length > 0) {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&email=admin@realproperties.com&accept-language=en`);
          const data = await res.json();
          if (data && data.address) {
            const fetchedCountry = data.address.country;
            const fetchedState = data.address.state;
            
            if (fetchedCountry) {
              const countryMatch = Object.keys(locationData).find(c => c.toLowerCase() === fetchedCountry.toLowerCase());
              if (countryMatch) {
                setSearchCountry(countryMatch);
                
                if (fetchedState) {
                  const stateMatch = Object.keys(locationData[countryMatch] || {}).find(s => s.toLowerCase() === fetchedState.toLowerCase());
                  if (stateMatch) {
                    setSearchState(stateMatch);
                    
                    const cityFields = ['city', 'town', 'village', 'municipality', 'county', 'suburb', 'neighbourhood', 'state_district', 'district'];
                    const addressValues = cityFields.map(field => data.address[field]).filter(val => typeof val === 'string');
                    const cityList = locationData[countryMatch][stateMatch] || [];
                    
                    let bestCityMatch = null;
                    for (const val of addressValues) {
                      if (val === fetchedCountry || val === fetchedState) continue;
                      let match = cityList.find(c => c.toLowerCase() === val.toLowerCase());
                      if (!match) {
                        match = cityList.find(c => val.toLowerCase().includes(c.toLowerCase()) || c.toLowerCase().includes(val.toLowerCase()));
                      }
                      if (match) {
                        bestCityMatch = match;
                        break;
                      }
                    }
                    if (bestCityMatch) {
                      setSearchCity(bestCityMatch);
                    }
                  }
                }
              }
            }
          }
        } catch (err) {}
      }, () => {});
    }
  }, [locationData]);


  const reversedFeatured = [...featuredProperties].reverse();
  const allPropertiesForSale = properties.filter(p => p.for !== 'Rent');
  const allWealthPropertiesForSale = reversedFeatured.filter(p => p.propertyType !== 'Rent');
  
  const allPropertiesForRent = properties.filter(p => p.for === 'Rent');
  const allWealthPropertiesForRent = reversedFeatured.filter(p => p.propertyType === 'Rent');

  const reversedFeaturedProjects = [...featuredProjects].reverse();

  const sortByImageCount = (a, b) => {
    const getImageCount = (p) => {
      let count = 0;
      if (p.images && p.images.length > 0) count += p.images.length;
      else if (p.coverImage || p.photo) count += 1;
      return count;
    };
    const countA = getImageCount(a);
    const countB = getImageCount(b);
    if (countA !== countB) return countB - countA;
    const dateA = new Date(a.createdAt || 0).getTime();
    const dateB = new Date(b.createdAt || 0).getTime();
    return dateB - dateA;
  };

  const newProperties = [...allPropertiesForSale, ...allWealthPropertiesForSale].sort(sortByImageCount).slice(0, 5);
  const rentalProperties = [...allPropertiesForRent, ...allWealthPropertiesForRent].sort(sortByImageCount).slice(0, 5);
  const combinedProjects = [...projects, ...reversedFeaturedProjects].slice(0, 5);
  const addedToday = [...properties.slice(3, 6), ...reversedFeatured.slice(3, 6)].sort(sortByImageCount).slice(0, 3);

  const { nearbyProperties, locationLabel } = (() => {
    const city = searchCity?.toLowerCase();
    if (city) {
      const localMatches = properties.filter(p => p.address?.city?.toLowerCase().includes(city));
      const wealthMatches = featuredProperties.filter(p => p.location?.toLowerCase().includes(city)).map(p => ({ ...p, isWealthProperty: true }));
      const combined = [...localMatches, ...wealthMatches].sort(sortByImageCount).slice(0, 5);
      if (combined.length > 0) return { nearbyProperties: combined, locationLabel: searchCity };
    }
    return { nearbyProperties: [], locationLabel: '' };
  })();

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    let queryParams = [];
    if (searchQuery) queryParams.push(`search=${encodeURIComponent(searchQuery)}`);
    if (searchCountry) queryParams.push(`country=${encodeURIComponent(searchCountry)}`);
    if (searchState) queryParams.push(`state=${encodeURIComponent(searchState)}`);
    if (searchCity) queryParams.push(`city=${encodeURIComponent(searchCity)}`);
    
    if (searchType) {
      if (searchType.includes('|')) {
        const [pType, sType] = searchType.split('|');
        queryParams.push(`propertyType=${encodeURIComponent(pType)}`);
        queryParams.push(`subCategories=${encodeURIComponent(sType)}`);
      } else {
        queryParams.push(`propertyType=${encodeURIComponent(searchType)}`);
      }
    }
    navigate(`/properties?${queryParams.join('&')}`);
  };

  const handleBannerClick = (url) => {
    if (!url) return;
    if (url.startsWith('http')) window.open(url, '_blank', 'noopener,noreferrer');
    else navigate(url);
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen pb-24 overflow-x-hidden">
        {/* Banner Skeleton */}
        <div className="w-full h-[220px] bg-gray-200 animate-pulse mb-6"></div>
        
        <div className="px-4">
          {/* Title & Search Skeleton */}
          <div className="h-8 bg-gray-200 rounded-md w-3/4 mb-2 animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded-md w-1/2 mb-6 animate-pulse"></div>
          <div className="h-12 bg-gray-200 rounded-full w-full mb-8 animate-pulse"></div>
          
          {/* Top Projects Skeleton */}
          <div className="flex justify-between items-center mb-4">
            <div className="h-6 bg-gray-200 rounded-md w-1/3 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded-md w-1/6 animate-pulse"></div>
          </div>
          
          {/* Horizontal Cards Skeleton */}
          <div className="flex gap-4 overflow-x-hidden mb-8">
            <div className="min-w-[280px] h-[250px] bg-gray-200 rounded-xl animate-pulse flex-shrink-0"></div>
            <div className="min-w-[280px] h-[250px] bg-gray-200 rounded-xl animate-pulse flex-shrink-0"></div>
          </div>
          
          {/* New Properties Skeleton */}
          <div className="flex justify-between items-center mb-4">
            <div className="h-6 bg-gray-200 rounded-md w-1/3 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded-md w-1/6 animate-pulse"></div>
          </div>
          
          {/* Vertical Cards Skeleton */}
          <div className="flex flex-col gap-4">
            <div className="w-full h-[140px] bg-gray-200 rounded-xl animate-pulse"></div>
            <div className="w-full h-[140px] bg-gray-200 rounded-xl animate-pulse"></div>
            <div className="w-full h-[140px] bg-gray-200 rounded-xl animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen overflow-x-hidden flex flex-col">
      
      {/* 1. Banners Section */}
      {banners.length > 0 ? (
        <div className="relative w-full h-[220px] bg-gray-200 overflow-hidden mb-6">
          {banners.map((banner, index) => (
            <img
              key={banner._id}
              src={getImageUrl(banner.imageUrl)}
              alt="Banner"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${index === currentBannerIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
              onClick={() => handleBannerClick(banner.linkUrl)}
            />
          ))}
          {/* Text Overlay */}
          {banners[currentBannerIndex]?.title && (
             <div className="absolute bottom-0 left-0 w-full p-4 pb-12 bg-gradient-to-t from-black/80 to-transparent z-20 pointer-events-none text-white">
                <h3 className="font-bold text-lg">{banners[currentBannerIndex].title}</h3>
                {banners[currentBannerIndex].description && (
                  <p className="text-xs opacity-90 line-clamp-1">{banners[currentBannerIndex].description}</p>
                )}
             </div>
          )}
          {banners.length > 1 && (
            <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 flex gap-2">
              {banners.map((_, i) => (
                <div key={i} className={`w-2 h-2 rounded-full transition-all duration-300 ${i === currentBannerIndex ? 'bg-primary w-4' : 'bg-white/60'}`} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="relative w-full h-[220px] bg-gray-200 overflow-hidden mb-6">
          <img src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1600&q=80&fm=webp" alt="Default Banner" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent z-10"></div>
          <div className="absolute bottom-0 left-0 w-full p-4 pb-12 z-20 text-white">
            <h3 className="font-bold text-lg">Find Your Dream Property</h3>
            <p className="text-xs opacity-90">Search millions of apartments and houses on Real Properties.</p>
          </div>
        </div>
      )}

      {/* Search Bar Section */}
      <div className="px-4 -mt-10 relative z-30 mb-6">
        <form onSubmit={handleSearch} className="flex flex-col gap-3 bg-white p-4 rounded-[20px] shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-gray-100">
          <div className="relative">
            <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
            <input
              type="text"
              placeholder="Search by keyword..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3.5 rounded-xl shadow-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm bg-gray-50"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <select 
              value={searchCountry} 
              onChange={e => {
                setSearchCountry(e.target.value);
                setSearchState('');
                setSearchCity('');
              }}
              className="w-full px-3 py-3 rounded-xl shadow-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm bg-gray-50"
            >
              <option value="">Country</option>
              {countries.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            
            <select 
              value={searchState} 
              onChange={e => {
                setSearchState(e.target.value);
                setSearchCity('');
              }} 
              disabled={!searchCountry}
              className="w-full px-3 py-3 rounded-xl shadow-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm bg-gray-50 disabled:bg-gray-100 disabled:opacity-70"
            >
              <option value="">State</option>
              {states.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            
            <select 
              value={searchCity} 
              onChange={e => setSearchCity(e.target.value)} 
              disabled={!searchState || cities.length === 0}
              className="w-full px-3 py-3 rounded-xl shadow-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm bg-gray-50 disabled:bg-gray-100 disabled:opacity-70"
            >
              <option value="">{searchState && cities.length === 0 ? "No cities" : "City"}</option>
              {cities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            
            <select 
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              className="w-full px-3 py-3 rounded-xl shadow-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm bg-gray-50"
            >
              <option value="">Property Type</option>
              {[...templates].sort((a, b) => {
                if (a.name === 'Residential') return -1;
                if (b.name === 'Residential') return 1;
                return a.name.localeCompare(b.name);
              }).map(t => (
                <optgroup key={t.name} label={t.name}>
                  {t.subCategories && t.subCategories.map(sub => (
                    <option key={sub.name} value={`${t.name}|${sub.name}`}>{sub.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          
          <button type="submit" className="w-full mt-2 bg-primary text-white py-3.5 rounded-xl font-bold shadow-md hover:bg-primary/90 transition-colors flex items-center justify-center gap-2">
            <FiSearch /> Search Properties
          </button>
        </form>
      </div>


      {/* Top Projects */}
      {combinedProjects.length > 0 && (
        <div className="mb-0 bg-white py-4 shadow-sm border-y border-gray-100">
          <div className="px-4 flex justify-between items-end mb-3">
            <h2 className="text-lg font-bold text-gray-900">Top Projects</h2>
            <Link to="/projects" className="text-primary text-xs font-semibold">View All</Link>
          </div>
          <div className="flex overflow-x-auto gap-4 px-4 pb-4 snap-x scrollbar-hide">
            {combinedProjects.map((project, i) => (
              <div key={project._id || i} className="w-[90vw] max-w-[340px] snap-center flex-shrink-0">
                <MobileProjectCard project={project} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Properties Near You */}
      {nearbyProperties.length > 0 && (
        <div className="mb-0 bg-orange-50/50 py-4 shadow-sm border-y border-orange-100">
          <div className="px-4 flex justify-between items-end mb-3">
            <div>
               <div className="text-[10px] font-bold text-primary uppercase tracking-wider flex items-center gap-1 mb-0.5"><FiMapPin size={10}/> Your Location</div>
               <h2 className="text-lg font-bold text-gray-900">Properties in <span className="text-primary">{locationLabel}</span></h2>
            </div>
          </div>
          <div className="flex overflow-x-auto gap-4 px-4 pb-4 snap-x scrollbar-hide">
            {nearbyProperties.map(property => (
              <div key={property._id} className="w-[90vw] max-w-[340px] snap-center flex-shrink-0">
                <MobilePropertyCard property={property} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New Properties */}
      {newProperties.length > 0 && (
        <div className="mb-0 bg-white py-4 shadow-sm border-y border-gray-100">
          <div className="px-4 flex justify-between items-end mb-3">
            <h2 className="text-lg font-bold text-gray-900">New Properties</h2>
            <Link to="/properties" className="text-primary text-xs font-semibold">View All</Link>
          </div>
          <div className="flex overflow-x-auto gap-4 px-4 pb-4 snap-x scrollbar-hide">
            {newProperties.map(property => (
              <div key={property._id} className="w-[90vw] max-w-[340px] snap-center flex-shrink-0">
                <MobilePropertyCard property={property} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rental Properties */}
      {rentalProperties.length > 0 && (
        <div className="mb-0 bg-white py-4 shadow-sm border-y border-gray-100">
          <div className="px-4 flex justify-between items-end mb-3">
            <h2 className="text-lg font-bold text-gray-900">Rental Properties</h2>
            <Link to="/rent" className="text-primary text-xs font-semibold">View All</Link>
          </div>
          <div className="flex overflow-x-auto gap-4 px-4 pb-4 snap-x scrollbar-hide">
            {rentalProperties.map(property => (
              <div key={property._id} className="w-[90vw] max-w-[340px] snap-center flex-shrink-0">
                <MobilePropertyCard property={property} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Builders */}
      {builders.length > 0 && (
        <div className="mb-0 py-4 bg-white border-y border-gray-100">
          <div className="px-4 mb-3">
            <h2 className="text-lg font-bold text-gray-900">Top Builders</h2>
          </div>
          <div className="flex overflow-x-auto gap-4 px-4 pb-4 snap-x scrollbar-hide">
            {builders.map(builder => (
              <div key={builder._id} className="w-[90vw] max-w-[340px] snap-center flex-shrink-0">
                 <BuilderCard builder={builder} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Added Today */}
      {addedToday.length > 0 && (
        <div className="mb-0 bg-white py-4 shadow-sm border-y border-gray-100">
          <div className="px-4 flex justify-between items-end mb-3">
            <h2 className="text-lg font-bold text-gray-900">Added Today</h2>
            <button className="text-[10px] font-bold text-primary border border-primary px-2 py-1 rounded-full">RECENT</button>
          </div>
          <div className="flex overflow-x-auto gap-4 px-4 pb-4 snap-x scrollbar-hide">
            {addedToday.map(property => (
              <div key={property._id} className="w-[90vw] max-w-[340px] snap-center flex-shrink-0">
                <MobilePropertyCard property={property} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Platform Statistics */}
      {siteStats && (
        <div className="bg-slate-900 text-white py-10 pb-8 px-4 mt-12 rounded-t-[40px] shadow-2xl relative flex-grow">
          <h2 className="text-2xl font-black mb-2 leading-tight">We only deliver results.</h2>
          <p className="text-slate-400 text-xs mb-8">Connecting buyers, sellers, and builders across the state. No excuses, just seamless real estate transactions.</p>
          
          <div className="grid grid-cols-2 gap-4 mb-8">
            {[
              { label: 'Buyers', count: siteStats.users?.buyer || 0 },
              { label: 'Sellers', count: siteStats.users?.seller || 0 },
              { label: 'Developers', count: siteStats.users?.builder || 0 },
              { label: 'Agents', count: siteStats.users?.agent || 0 },
            ].map((stat, idx) => (
              <div key={idx} className="bg-slate-800/50 p-4 rounded-2xl border border-slate-700/50 text-center">
                <div className="text-2xl font-black text-white">{stat.count}{stat.count > 0 && '+'}</div>
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
          
          <h2 className="text-xl font-black mb-4">Properties Overview</h2>
          <div className="flex flex-col gap-3">
             {Object.keys(siteStats.properties || {}).sort().map((type, idx) => {
                const props = siteStats.properties[type];
                return (
                  <div key={type} className="bg-slate-800 p-4 rounded-2xl border border-slate-700 flex justify-between items-center">
                    <div>
                      <h3 className="font-bold text-sm">{type}{(type.endsWith('Properties') || type.endsWith('Projects')) ? '' : ' Properties'}</h3>
                      <div className="text-[10px] text-slate-400 mt-1">
                        <span className="text-emerald-400">{props.available} Available</span> • <span className="text-rose-400">{props.sold} Sold</span>
                      </div>
                    </div>
                    <div className="text-xl font-black text-primary bg-primary/10 w-10 h-10 rounded-full flex items-center justify-center">
                      {props.uploaded}
                    </div>
                  </div>
                );
             })}
          </div>
        </div>
      )}
      
    </div>
  );
};

export default MobileHome;
